import {
  CANDY_MACHINE_BOT_BADGE_ADDRESS,
  DCKSLAP_COMPONENT_ADDRESS,
  DCKSLAP_RESOURCE_ADDRESS,
  DCKSLAP_USER_BADGE_RESOURCE_ADDRESS,
  RADIX_API_GATEWAY_URL,
} from "../../../constants";

const STORAGE_SCOPE = RADIX_API_GATEWAY_URL.includes("stokenet")
  ? "stokenet"
  : "mainnet";
const SWAP_PULSE_CACHE_KEY = `swapExplorerPulse.${STORAGE_SCOPE}.v1`;
const SWAP_PULSE_CACHE_TTL_MS = 3 * 60 * 1000;

const EMPTY_PULSE_PAYLOAD = {
  items: [],
  fetchedAt: null,
  isFresh: false,
};

let inFlightPulsePromise = null;

const isBrowser = typeof window !== "undefined";

const getStoredValue = (key) => {
  if (!isBrowser) return null;
  return window.localStorage.getItem(key);
};

const setStoredValue = (key, value) => {
  if (!isBrowser) return;
  window.localStorage.setItem(key, value);
};

const parseJson = (value, fallback = null) => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn("Failed to parse cached swap pulse payload:", error);
    return fallback;
  }
};

const postGateway = async (body) => {
  const response = await fetch(`${RADIX_API_GATEWAY_URL}/stream/transactions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch swap pulse (${response.status})`);
  }

  return response.json();
};

const toAbsoluteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.abs(parsed) : 0;
};

const formatAmount = (value) => {
  if (!Number.isFinite(value)) {
    return "0";
  }

  if (value >= 1000) {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }

  if (value % 1 === 0) {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    }).format(value);
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);
};

const shortenAddress = (address) => {
  if (!address || address.length < 18) {
    return address || "unknown account";
  }

  return `${address.slice(0, 12)}...${address.slice(-6)}`;
};

const getPrimaryAccountEntity = (item) => {
  const fungibleEntities =
    item?.balance_changes?.fungible_balance_changes?.map((change) => change.entity_address) || [];
  const nonFungibleEntities =
    item?.balance_changes?.non_fungible_balance_changes?.map((change) => change.entity_address) || [];

  return [...fungibleEntities, ...nonFungibleEntities].find(
    (entityAddress) => typeof entityAddress === "string" && entityAddress.startsWith("account_")
  );
};

const getDckslapTransferAmount = (item) => {
  const changes = item?.balance_changes?.fungible_balance_changes || [];
  const matchingChange = changes.find(
    (change) => change.resource_address === DCKSLAP_RESOURCE_ADDRESS
  );

  return matchingChange ? toAbsoluteNumber(matchingChange.balance_change) : 0;
};

const normalizePulsePayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return EMPTY_PULSE_PAYLOAD;
  }

  const fetchedAtMs = Date.parse(payload.fetchedAt);

  return {
    items: Array.isArray(payload.items) ? payload.items : [],
    fetchedAt: payload.fetchedAt || null,
    isFresh: Number.isFinite(fetchedAtMs) && Date.now() - fetchedAtMs < SWAP_PULSE_CACHE_TTL_MS,
  };
};

const mapWithdrawalItem = (item) => {
  const account = getPrimaryAccountEntity(item);
  const withdrawalAmount = getDckslapTransferAmount(item);

  return {
    id: `withdrawal-${item.intent_hash || item.state_version}`,
    kind: "withdrawal",
    kindLabel: "Withdrawal",
    title: withdrawalAmount
      ? `${formatAmount(withdrawalAmount)} DCKSLAP moved out`
      : "DCKSLAP withdrawal settled",
    summary: account
      ? `A successful player withdrawal settled for ${shortenAddress(account)}.`
      : "A successful DCKSLAP withdrawal settled on-ledger.",
    confirmedAt: item.confirmed_at || item.round_timestamp || null,
    feePaid: item.fee_paid || null,
    stateVersion: item.state_version || null,
    intentHash: item.intent_hash || null,
  };
};

const mapBotActivityItem = (item) => {
  const manifestInstructions = item?.manifest_instructions || "";
  const account = getPrimaryAccountEntity(item);

  if (manifestInstructions.includes("send_candies")) {
    return {
      id: `game-${item.intent_hash || item.state_version}`,
      kind: "game",
      kindLabel: "Game Round",
      title: "Candy machine round settled",
      summary: account
        ? `A new candy-machine result closed out for ${shortenAddress(account)}.`
        : "A successful candy-machine round just closed on-ledger.",
      confirmedAt: item.confirmed_at || item.round_timestamp || null,
      feePaid: item.fee_paid || null,
      stateVersion: item.state_version || null,
      intentHash: item.intent_hash || null,
    };
  }

  if (manifestInstructions.includes("mint_dckuserbadge")) {
    return {
      id: `badge-${item.intent_hash || item.state_version}`,
      kind: "badge",
      kindLabel: "New Player",
      title: "New DckSlap badge minted",
      summary: account
        ? `A fresh player badge landed in ${shortenAddress(account)}.`
        : "A new DckSlap player badge was minted successfully.",
      confirmedAt: item.confirmed_at || item.round_timestamp || null,
      feePaid: item.fee_paid || null,
      stateVersion: item.state_version || null,
      intentHash: item.intent_hash || null,
    };
  }

  return {
    id: `automation-${item.intent_hash || item.state_version}`,
    kind: "automation",
    kindLabel: "Automation",
    title: "Background utility transaction",
    summary:
      "A supporting DckSlap automation transaction landed successfully on the Radix ledger.",
    confirmedAt: item.confirmed_at || item.round_timestamp || null,
    feePaid: item.fee_paid || null,
    stateVersion: item.state_version || null,
    intentHash: item.intent_hash || null,
  };
};

const writeCachedPulsePayload = (payload) => {
  setStoredValue(SWAP_PULSE_CACHE_KEY, JSON.stringify(payload));
};

export const getCachedSwapExplorerPulse = () =>
  normalizePulsePayload(parseJson(getStoredValue(SWAP_PULSE_CACHE_KEY)));

export const fetchSwapExplorerPulse = async () => {
  if (inFlightPulsePromise) {
    return inFlightPulsePromise;
  }

  inFlightPulsePromise = (async () => {
    const [withdrawalsData, botActivityData] = await Promise.all([
      postGateway({
        limit_per_page: 5,
        order: "Desc",
        transaction_status_filter: "Success",
        manifest_badges_presented_filter: [DCKSLAP_USER_BADGE_RESOURCE_ADDRESS],
        events_filter: [
          {
            event: "Withdrawal",
            resource_address: DCKSLAP_RESOURCE_ADDRESS,
          },
        ],
        affected_global_entities_filter: [DCKSLAP_COMPONENT_ADDRESS],
        opt_ins: {
          balance_changes: true,
          receipt_output: false,
        },
      }),
      postGateway({
        limit_per_page: 8,
        order: "Desc",
        transaction_status_filter: "Success",
        manifest_badges_presented_filter: [CANDY_MACHINE_BOT_BADGE_ADDRESS],
        opt_ins: {
          balance_changes: true,
          manifest_instructions: true,
          receipt_output: false,
        },
      }),
    ]);

    const pulseItems = [
      ...(withdrawalsData?.items || []).map(mapWithdrawalItem),
      ...(botActivityData?.items || []).map(mapBotActivityItem),
    ]
      .filter(Boolean)
      .sort((left, right) => {
        const leftTimestamp = Date.parse(left.confirmedAt || "") || 0;
        const rightTimestamp = Date.parse(right.confirmedAt || "") || 0;

        if (leftTimestamp !== rightTimestamp) {
          return rightTimestamp - leftTimestamp;
        }

        return (right.stateVersion || 0) - (left.stateVersion || 0);
      })
      .slice(0, 6);

    const payload = {
      items: pulseItems,
      fetchedAt: new Date().toISOString(),
    };

    writeCachedPulsePayload(payload);

    return {
      ...normalizePulsePayload(payload),
      isFresh: true,
    };
  })();

  try {
    return await inFlightPulsePromise;
  } finally {
    inFlightPulsePromise = null;
  }
};
